import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Button } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioMedicoBase } from '@/features/registro/medicos/relatorios/relatorio-medico.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { CardMedicoSelecionado } from '@/features/registro/medicos/relatorios/components/card-medico-selecionado/card-medico-selecionado';
import { DeclaracaoMedicoConvocadoMedicoService } from '@/core/api/sicom-api/registro/medico/relatorios/declaracao-medico-convocado-medico/declaracao-medico-convocado-medico.service';
import { ReportDeclaracaoMedicoConvocadoMedicoRequest, ReportDeclaracaoMedicoConvocadoMedicoResponse } from '@/core/api/sicom-api/registro/medico/relatorios/declaracao-medico-convocado-medico/declaracao-medico-convocado-medico.model';
import { DeclaracaoMedicoConvocadoMedicoJobService } from '@/core/services/declaracao-medico-convocado-medico-job.service';
import { CanDirective } from '@/core/permissions/can.directive';

type Req = ReportDeclaracaoMedicoConvocadoMedicoRequest;
type Resp = ReportDeclaracaoMedicoConvocadoMedicoResponse;

@Component({
    selector: 'app-declaracao-medico-convocado-medico',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, TooltipModule, RelatorioContainer, Pesquisa, CardMedicoSelecionado, CanDirective],
    templateUrl: './declaracao-medico-convocado-medico.html',
    styleUrl: './declaracao-medico-convocado-medico.scss'
})
/**
 * Relatório PDF assíncrono da Declaração de Médico Convocado (nuCrm/sgUf, sem assinante).
 * Herda `RelatorioMedicoBase`: toda a orquestração de export/polling/download/erro e o card
 * "médico selecionado + Alterar" vêm da base; aqui só resolvemos a identificação do médico e
 * delegamos o HTTP ao service.
 */
export class ReportDeclaracaoMedicoConvocadoMedicoComponent extends RelatorioMedicoBase<Req, Resp> {
    private readonly declaracaoService = inject(DeclaracaoMedicoConvocadoMedicoService);
    readonly declaracaoJobService = inject(DeclaracaoMedicoConvocadoMedicoJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.declaracaoJobService.job$;

    protected get jobFacade(): RelatorioJobFacade {
        return this.declaracaoJobService;
    }

    /** Monta o request a partir da pessoa selecionada; retorna null (e avisa) se faltar CRM/UF. */
    protected buildRequest(): Req | null {
        const medico = this.resolverIdentificacaoMedico('Selecione um médico (CRM/UF) antes de gerar a declaração.');
        if (!medico) return null;
        const { nuCrm, sgUf } = medico;

        return { nuCrm, sgUf };
    }

    protected exportar(request: Req) {
        return this.declaracaoService.exportarPdf(request);
    }

    /** Limpa o job/preview corrente (a declaração não tem formulário de dados a resetar). */
    limparFormulario(): void {
        this.limparJob();
    }
}
